import React from 'react';

/**
 * Tailwind v3 — Accessible Switch component (React)
 * Features:
 * - Controlled via `checked` + `onChange`
 * - Keyboard accessible (Space / Enter)
 * - Sizes: 'sm' | 'md' | 'lg'
 * - Disabled state
 * - Pure Tailwind classes (v3)
 *
 * Usage:
 * <Switch checked={isOn} onChange={(val) => setIsOn(val)} label="Notifications" />
 */

const SIZE_MAP = {
    sm: {
        track: 'w-9 h-5',
        thumb: 'w-4 h-4 translate-x-0.5',
        thumbChecked: 'translate-x-4',
    },
    md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5 translate-x-0.5',
        thumbChecked: 'translate-x-5',
    },
    lg: {
        track: 'w-14 h-8',
        thumb: 'w-6 h-6 translate-x-0.5',
        thumbChecked: 'translate-x-6',
    },
};

export function Switch({
    checked = false,
    onChange = () => { },
    disabled = false,
    size = 'md',
    label = null,
    id = undefined,
    ariaLabel = undefined,
    isLabelLeft = false
}) {
    const s = SIZE_MAP[size] || SIZE_MAP['md'];

    function toggle() {
        if (disabled) return;
        onChange(!checked);
    }

    function handleKeyDown(e) {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
        }
    }

    return (
        <div className="inline-flex items-center gap-3">
            {
                isLabelLeft ? (
                    <label htmlFor={id} className={`select-none ${disabled ? 'opacity-50' : ''}`}>
                        {label}
                    </label>
                ) : null
            }

            <button
                id={id}
                role="switch"
                type="button"
                aria-checked={checked}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={toggle}
                onKeyDown={handleKeyDown}
                className={
                    `relative inline-flex items-center flex-shrink-0 rounded-full transition-colors focus:outline-none ` +
                    `focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-yellow-500 ` +
                    `${s.track} ` +
                    `${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ` +
                    `${checked ? 'bg-yellow-600' : 'bg-gray-200 dark:bg-gray-700'}`
                }
            >
                <span
                    aria-hidden="true"
                    className={`inline-block transform rounded-full bg-white shadow ring-0 transition-transform ${s.thumb} ${checked ? s.thumbChecked : ''}`}
                />
            </button>

            {
                (label && !isLabelLeft) ? (
                    <label htmlFor={id} className={`select-none ${disabled ? 'opacity-50' : ''}`}>
                        {label}
                    </label>
                ) :
                    null
            }
        </div>
    );
}
